import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, map, of, shareReplay, take, tap } from 'rxjs';
import { ConfigService } from './config.service';
import {
  CHARTER_COLOR_FALLBACK,
  CHARTER_COLOR_FALLBACK_DARK,
  readCharterColorCache,
  readThemeMode,
  ThemeMode,
  writeCharterColorCache,
  writeThemeMode,
} from '../constants/charter-color.defaults';
import { CharterColor } from '../models/charter-color.model';
import { ApiConfigService } from './api-config.service';
import { ThemeService } from './theme.service';

@Injectable({ providedIn: 'root' })
export class CharterColorService {
  private allColors$: Observable<CharterColor[]> | null = null;
  private readonly modeSubject = new BehaviorSubject<ThemeMode>(readThemeMode());

  readonly mode$ = this.modeSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly apiConfig: ApiConfigService,
    private readonly theme: ThemeService,
    private readonly configService: ConfigService,
  ) {}

  /** Charge la charte (cache local → API → fallback) et l'applique au DOM. */
  load(): Observable<CharterColor> {
    const mode = readThemeMode();
    this.modeSubject.next(mode);
    this.applyCachedOrFallback(mode);

    return this.fetchAllColors().pipe(
      map((colors) => this.applyMode(colors, mode)),
      take(1),
    );
  }

  getMode(): ThemeMode {
    return this.modeSubject.value;
  }

  isDarkMode(): boolean {
    return this.getMode() === 'dark';
  }

  /** Bascule light ↔ dark (comme mobile PreferenceKeys.THEME_MODE). */
  setMode(mode: ThemeMode): Observable<CharterColor> {
    writeThemeMode(mode);
    this.modeSubject.next(mode);

    return this.fetchAllColors().pipe(
      map((colors) => this.applyMode(colors, mode)),
      take(1),
    );
  }

  toggleMode(): Observable<CharterColor> {
    return this.setMode(this.isDarkMode() ? 'light' : 'dark');
  }

  getCurrent(): CharterColor {
    return this.theme.getCurrent();
  }

  getAccentPalette(): string[] {
    return this.theme.getAccentPalette();
  }

  clearCache(): void {
    this.allColors$ = null;
  }

  private fetchAllColors(): Observable<CharterColor[]> {
    if (!this.allColors$) {
      this.allColors$ = this.http.get<CharterColor[]>(this.apiConfig.colorListUrl).pipe(
        catchError((err) => {
          console.warn('[CharterColor] API unavailable, using fallbacks', err);
          return of([CHARTER_COLOR_FALLBACK, CHARTER_COLOR_FALLBACK_DARK]);
        }),
        shareReplay(1),
      );
    }

    return this.allColors$;
  }

  private applyCachedOrFallback(mode: ThemeMode): void {
    const cached = readCharterColorCache();
    if (cached?.mode === mode) {
      this.theme.apply(cached);
      return;
    }

    this.theme.apply(mode === 'dark' ? CHARTER_COLOR_FALLBACK_DARK : CHARTER_COLOR_FALLBACK);
  }

  private applyMode(colors: CharterColor[], mode: ThemeMode): CharterColor {
    const color = this.pickByMode(colors, mode);
    writeCharterColorCache(color);
    this.theme.apply(color);

    if (this.configService.enableApiLogs) {
      console.log('[CharterColor] applied', color.mode, color.principal, color.secondary);
    }

    return color;
  }

  private pickByMode(colors: CharterColor[], mode: ThemeMode): CharterColor {
    const match = colors.find((c) => c.mode === mode);
    if (match) {
      return match;
    }

    if (colors.length > 0) {
      return colors[0];
    }

    return mode === 'dark' ? CHARTER_COLOR_FALLBACK_DARK : CHARTER_COLOR_FALLBACK;
  }
}
